import {
  useCallback,
  useRef,
} from 'react';
import * as is from 'is';
import FileSaver from 'file-saver';
import { useStreamConverter } from '../converters/stream';
import { CSVProcessor } from './csv-processor';
import * as events from './events';

export const MAX_NUM_ROWS = 3000;

export const useCSVFileUpload = (statusColumn = 'Status', messageColumn = 'Message') => {
  const converter = useStreamConverter();
  const createStream = converter.shift();
  const headerColumns = converter.pop();
  const fileStorage = useRef([]);
  const documentStorage = useRef([]);
  const abort = useRef(false);
  const loadFile = useCallback(
    (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const content = event.target.result;
            const processor = new CSVProcessor();
            processor.process(content);
            if (!processor.isValid(headerColumns)) {
              throw new Error('Processed file does not contain the proper headers.');
            }
            if (processor.numRows > MAX_NUM_ROWS) {
              throw new Error('Maximum number of streams allowed exceeded.');
            }
            resolve({ filename: file.name, processor });
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = (event) => {
          reject(event.target.result);
        };
        reader.readAsText(file);
      });
    },
    [headerColumns]
  );
  const saveFiles = useCallback(
    async (fileList) => {
      if (!(fileList instanceof FileList)) {
        return;
      }
      if (fileList.length === 0) {
        fileStorage.current = undefined;
        fileStorage.current = [];
        return;
      }
      if (!is.empty(fileStorage.current)) {
        fileStorage.current = []; // clear the current file definitions.
      }
      for (const file of fileList) {
        const definition = await loadFile(file);
        fileStorage.current.push(definition);
      }
      documentStorage.current = []; // clear the document storage.
    },
    [fileStorage, loadFile]
  );
  const createStreams = useCallback(
    async () => {
      window.dispatchEvent(new events.StartProcessingCSVFilesEvent());
      while (fileStorage.current.length > 0) {
        const item = fileStorage.current.shift();
        const {
          filename,
          processor,
        } = item;
        if (abort.current) {
          abort.current = false;
          break;
        }
        if (is.undef(filename)) {
          continue;
        }
        window.dispatchEvent(new events.ProcessingCSVFileEvent(filename));
        try {
          processor.addHeaderColumn(messageColumn);
          processor.addHeaderColumn(statusColumn);
          window.dispatchEvent(new events.ProcessingRowsEvent(processor.numRows));
          for (const row of processor) {
            if (abort.current) {
              abort.current = false;
              break;
            }
            try {
              const stream = await createStream(row);
              row[statusColumn] = 'success';
              row[messageColumn] = stream.id;
              window.dispatchEvent(new events.ProcessedCSVRowEvent());
            } catch (error) {
              row[statusColumn] = 'fail';
              row[messageColumn] = error;
              window.dispatchEvent(new events.ProcessedCSVRowEvent({ success: false, error }));
            }
          }
          documentStorage.current.push([processor.asStringDocument(), filename]);
          processor.initialize(); // clear out the memory.
        } catch (error) {
          window.dispatchEvent(new events.ProcessingErrorEvent(error));
        } finally {
          window.dispatchEvent(new events.EndProcessingCSVFileEvent(filename));
        }
      }
      if (abort.current) {
        abort.current = false;
      }
      window.dispatchEvent(new events.EndProcessingCSVFilesEvent());
      fileStorage.current.length = 0; // clear out the array.
    },
    [
      fileStorage,
      messageColumn,
      statusColumn,
      createStream,
    ]
  );
  const downloadFiles = useCallback(
    async () => {
      while (documentStorage.current.length > 0) {
        const [document, filename] = documentStorage.current.shift();
        const filenameTokens = filename.split('.');
        const extension = filenameTokens.pop();
        const resultsFilename = filenameTokens.concat('results', extension).join('.');
        const blob = new Blob([document], { type: 'text/plain;charset=utf-8' });
        FileSaver.saveAs(blob, resultsFilename);
      }
      documentStorage.current.length = 0;
    },
    [documentStorage]
  );
  const cancelProcess = useCallback(
    () => {
      abort.current = true;
    },
    [abort]
  );
  return [
    saveFiles,
    createStreams,
    downloadFiles,
    cancelProcess,
  ];
};
